import { CloseRounded } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { changePassword } from '../../../../redux/actions/auth'
import { formValidator, resetFormValidator } from '../../../../redux/actions/validator'
import { IRootState } from '../../../../redux/types'
import { CancelButton, SaveButton } from '../../../shared/components/inputs'
import useScreenWidth from '../../../shared/hooks/useScreenWidth'
import { formsStyles } from '../../../shared/styles/FormsStyles'
import { StyledDialog } from '../../../shared/styles/StyledDialog'

type UserProfileProps = {
  showForm: boolean
  setShowForm: any
}

function UserProfile({ showForm, setShowForm }: UserProfileProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const theme = useTheme()
  const formClasses = formsStyles(theme)

  const dispatch = useDispatch()
  const { isValidForm, errorForm, msgForm } = useSelector((state: IRootState) => state.validator)

  const [userPassword, setUserPassword] = useState({
    oldPassword: '',
    password: '',
    confirmPassword: '',
    showPassword: false
  })

  useEffect(() => {
    if (isValidForm && errorForm.password === false && errorForm.confirmPassword === false) {
      dispatch(resetFormValidator())
      dispatch(changePassword({ userPassword }))
      setUserPassword({
        oldPassword: '',
        password: '',
        confirmPassword: '',
        showPassword: false
      })
      setShowForm()
    }
  }, [isValidForm])

  const handleChangePassword = (inputName: string) => (event: any) => {
    setUserPassword({ ...userPassword, [inputName]: event.target.value })
  }
  const handleSubmitPassword = () => {
    dispatch(
      formValidator({
        password: [userPassword.password, 'required|max:45|min:3'],
        confirmPassword: [userPassword.confirmPassword, 'required|max:45|min:3|equal:' + userPassword.password]
      })
    )
  }

  return (
    <StyledDialog
      data-testid="change-password-modal"
      open={showForm}
      onClose={() => setShowForm()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">{t('login.change-password')}</Typography>
          <Button onClick={() => setShowForm()} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>
      <DialogContent sx={formClasses.bodyForm}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              data-testid="current-password"
              label={t('login.current-password')}
              type={userPassword.showPassword ? 'text' : 'password'}
              fullWidth
              required
              margin="normal"
              value={userPassword.oldPassword}
              onChange={handleChangePassword('oldPassword')}
              InputLabelProps={{ shrink: true }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              data-testid="new-password"
              label={t('login.new-password')}
              type={userPassword.showPassword ? 'text' : 'password'}
              fullWidth
              required
              margin="normal"
              value={userPassword.password}
              error={errorForm.password}
              helperText={msgForm.password}
              onChange={handleChangePassword('password')}
              InputLabelProps={{ shrink: true }}
              variant="filled"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              data-testid="confirm-password"
              label={t('login.confirm-password')}
              type={userPassword.showPassword ? 'text' : 'password'}
              fullWidth
              required
              margin="normal"
              value={userPassword.confirmPassword}
              error={errorForm.confirmPassword}
              helperText={msgForm.confirmPassword}
              onChange={handleChangePassword('confirmPassword')}
              InputLabelProps={{ shrink: true }}
              variant="filled"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <CancelButton data-testid="cancel-changes" onClick={() => setShowForm()}>
          {t('cancel')}
        </CancelButton>
        <SaveButton data-testid="save-changes" id="btn-handlelogin" onClick={() => handleSubmitPassword()}>
          {t('login.change-password')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default UserProfile
